import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Mail, MessageSquare, CheckCircle2, Clock, StickyNote, Save, Trash2 } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';

const fmtFull = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso || '';
  }
};

const ThreadItem = ({ item }) => {
  const isContact = item._type === 'contact';
  const Icon = isContact ? Mail : MessageSquare;
  const replied = item.status === 'replied';
  const headerText = isContact
    ? `Contact message${item.category ? ` · ${item.category}` : ''}`
    : `Quote request · ${item.service_type}${item.firearm_type ? ` (${item.firearm_type})` : ''}`;
  const body = isContact ? item.message : item.description;

  return (
    <div className="flex gap-3 pb-5 border-b border-slate-800 last:border-0 last:pb-0">
      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isContact ? 'bg-red-600/20 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-medium text-white">{headerText}</span>
          {replied ? (
            <Badge variant="secondary" className="bg-green-600/20 text-green-400 border-green-600/30 gap-1">
              <CheckCircle2 className="w-3 h-3" /> Replied
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30 gap-1">
              <Clock className="w-3 h-3" /> Pending
            </Badge>
          )}
          {!isContact && item.timeline && (
            <span className="text-xs text-gray-500">Timeline: {item.timeline}</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-2">{fmtFull(item.created_at)}</p>
        {body && <p className="text-sm text-gray-300 whitespace-pre-wrap">{body}</p>}
      </div>
    </div>
  );
};

const NoteEditor = ({ email, initialNote, initialUpdatedAt }) => {
  const [note, setNote] = useState(initialNote || '');
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(initialUpdatedAt || null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setNote(initialNote || '');
    setUpdatedAt(initialUpdatedAt || null);
    setDirty(false);
  }, [initialNote, initialUpdatedAt, email]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/api/customer-notes`,
        { email, notes: note },
        { headers: authHeaders() },
      );
      setUpdatedAt(data.updated_at);
      setDirty(false);
      toast.success('Note saved');
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded border border-slate-700 bg-slate-950/60 p-3" data-testid="customer-note-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <StickyNote className="w-4 h-4 text-amber-400" /> Quick notes
        </div>
        {updatedAt && (
          <span className="text-[10px] text-gray-500">
            saved {new Date(updatedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
      </div>
      <Textarea
        data-testid="customer-note-input"
        value={note}
        onChange={(e) => { setNote(e.target.value); setDirty(true); }}
        rows={2}
        placeholder="e.g. Prefers text over email · Picked up rifle 3/15"
        className="bg-slate-800 border-slate-700 text-white text-sm"
      />
      <div className="flex justify-end mt-2">
        <Button
          size="sm"
          data-testid="customer-note-save"
          disabled={saving || !dirty}
          onClick={save}
          className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
        >
          <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

const ConversationDialog = ({ open, onOpenChange, email, onThreadDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !email) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: resp } = await axios.get(
          `${API}/api/conversations`,
          { headers: authHeaders(), params: { email } },
        );
        if (!cancelled) setData(resp);
      } catch (err) {
        toast.error(`Failed to load thread: ${apiErr(err)}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, email]);

  const deleteEntireThread = async () => {
    if (!email || !data) return;
    const total = (data.contact_count || 0) + (data.quote_count || 0);
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Permanently delete the entire thread for ${data.name || email}?\n\n` +
      `This will remove ${data.contact_count || 0} contact message(s), ${data.quote_count || 0} quote request(s), and any internal notes. Total: ${total} item(s).\n\n` +
      `This cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/conversations`, {
        headers: authHeaders(),
        params: { email },
      });
      toast.success('Thread deleted');
      onThreadDeleted?.();
    } catch (err) {
      toast.error(`Delete failed: ${apiErr(err)}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Conversation thread
            {data?.name && <span className="text-gray-400 font-normal"> · {data.name}</span>}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {email}
            {data && ` · ${data.contact_count} contact${data.contact_count === 1 ? '' : 's'}, ${data.quote_count} quote${data.quote_count === 1 ? '' : 's'}`}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-gray-400">Loading…</p>
        ) : data ? (
          <div className="space-y-4 mt-2">
            <NoteEditor
              email={data.email}
              initialNote={data.notes}
              initialUpdatedAt={data.notes_updated_at}
            />
            {data.items.length > 0 ? (
              <div className="space-y-5" data-testid="thread-items">
                {data.items.map((it) => <ThreadItem key={`${it._type}-${it.id}`} item={it} />)}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No prior interactions found.</p>
            )}
            <div className="pt-4 border-t border-slate-800 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                data-testid="delete-thread-btn"
                onClick={deleteEntireThread}
                disabled={deleting || ((data.contact_count || 0) + (data.quote_count || 0) === 0)}
                className="border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300"
              >
                <Trash2 className="w-3 h-3 mr-2" />
                {deleting ? 'Deleting…' : 'Delete entire thread'}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ConversationDialog;
